// ---- Full-time reveal — GRID-LEVEL orchestration ----
// The reveal is a grid concern, not a card concern: a card cannot dim its
// siblings, and effects that radiate past a card's box (spotlight,
// shockwaves, particle burst) can't live inside a clipped/transformed card.
// So the grid is the conductor — it owns the timeline and the effects layer;
// cards receive their phase and their spotlit flag and just render. Ticket 0418
// removed the scrim and the sibling dim: separation comes from a bloom that
// brightens the neighbours, so `spotlitIds` now only lifts the exact card above
// them in the stack. ONE clock: the conductor flips a card to `active` and fires that card's
// grid effects in the SAME callback, so the card's CSS (which starts on the
// class flip, delay 0) and the spotlight can't drift.
//
// Extracted from GameweekGrid.tsx.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { scorePrediction } from '../../scoring'
import { prefersReducedMotion } from '../../scoreCountUp'
import type { SimGameStatus } from '../../gameweekSimulation'
import {
  computeRevealPlan,
  categoryForBasePoints,
  PHASE_A_SLAM_LAND_MS,
  type RevealCategory,
  type RevealInput,
} from '../../revealSequence'
import type { Game, Prediction } from '../../types'
import type { ScoreLine } from './gameweekPoints'

// ---- Full-time reveal timing ----
// How long each category's flourish holds the "active" (mid-reveal) state
// before the card drops back to plain settled. Must cover that category's CSS
// animations; the exact-score windows also gate the spotlight duration. These
// are the single source of truth for the conductor's clock — the card's
// CSS starts when the grid flips it to `active`, so the two never drift.
const REVEAL_ACTIVE_MS: Record<RevealCategory, number> = {
  miss: 900,
  partial: 520,
  outcome: 720,
  exact: 2100,
}
const VOLLEY_ACTIVE_MS = 2500
// How long the exact card stays lifted above its neighbours — the opening beat,
// not the whole of Phase A. The card finishes its lift/flare/ring/points-roll in
// place after that. Decoupled from the active window above on purpose.
const SPOTLIGHT_SOLO_MS = 950
const SPOTLIGHT_VOLLEY_MS = 1150
// Lifetime of a grid-layer burst before it's unmounted. Longer than it was: the
// bloom runs to 1600ms and the slowest mote to ~2600ms after its own delay.
const BURST_LIFETIME_MS = 3100

export type RevealPhase = 'pending' | 'active'

export interface RevealBurst {
  key: string
  cx: number
  cy: number
  size: number
  volley: boolean
}

export interface RevealCascade {
  revealPhaseById: Record<string, RevealPhase>
  spotlitIds: Set<string>
  volleyedIds: Set<string>
  bursts: RevealBurst[]
  /** Ref callback for each GameCard — the conductor measures these rects to place bursts. */
  registerCard: (gameId: string, el: HTMLButtonElement | null) => void
  /** Attach to the effects layer: burst positions are measured relative to it. */
  revealLayerRef: React.RefObject<HTMLDivElement | null>
}

export function useRevealCascade({
  games,
  predictions,
  actualResults,
  resultsRevealed,
  simGameStatuses,
  revealSeenGameIds,
  onMarkRevealSeen,
}: {
  games: Game[]
  predictions: Record<string, Prediction>
  actualResults: Record<string, ScoreLine>
  resultsRevealed: boolean
  simGameStatuses?: Record<string, SimGameStatus>
  revealSeenGameIds: Set<string> | null
  onMarkRevealSeen: (gameIds: string[], persist?: boolean) => void
}): RevealCascade {
  const [revealPhaseById, setRevealPhaseById] = useState<Record<string, RevealPhase>>({})
  const [spotlitIds, setSpotlitIds] = useState<Set<string>>(new Set())
  const [volleyedIds, setVolleyedIds] = useState<Set<string>>(new Set())
  const [bursts, setBursts] = useState<RevealBurst[]>([])

  const revealStartedRef = useRef<Set<string>>(new Set())
  const revealTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  // Ids that have been committed to `pending` but whose timer to flip them to
  // `active` hasn't fired yet — i.e. ids "at risk" if that timer gets
  // cancelled out from under them (see cleanup below). Removed the instant
  // their `active` timer actually runs.
  const pendingRevealIdsRef = useRef<Set<string>>(new Set())
  const cardRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const revealLayerRef = useRef<HTMLDivElement>(null)
  const burstKeyRef = useRef(0)
  const mountedRef = useRef(true)
  const onMarkRevealSeenRef = useRef(onMarkRevealSeen)
  useEffect(() => {
    onMarkRevealSeenRef.current = onMarkRevealSeen
  }, [onMarkRevealSeen])

  useEffect(() => {
    mountedRef.current = true
    // The ref array is only ever pushed to (never reassigned), so this captured
    // reference is the same live array the conductor keeps scheduling into.
    const timers = revealTimersRef.current
    // Same story for both sets: created once and only ever mutated (add/delete/
    // clear), never reassigned, so these locals are the same live Sets the
    // conductor works with.
    const pendingRevealIds = pendingRevealIdsRef.current
    const revealStarted = revealStartedRef.current
    return () => {
      mountedRef.current = false
      timers.forEach(clearTimeout)
      // Under StrictMode's dev-only mount replay, this cleanup runs
      // synthetically (same fiber, refs/state preserved) before the effects
      // re-run. Any id still `pending` at this point had its flip-to-`active`
      // timer just cancelled above, and `revealStartedRef` would otherwise
      // keep it marked "already scheduled" forever — permanently stuck
      // invisible with nothing left to ever animate it in. Roll
      // those ids back so the batch scheduler re-adopts and reschedules them
      // on the replay (or, on a real unmount, this is simply moot).
      for (const id of pendingRevealIds) {
        revealStarted.delete(id)
      }
      pendingRevealIds.clear()
    }
  }, [])

  // useLayoutEffect (not useEffect) so newly-settled cards are flipped to
  // `pending` (hidden) BEFORE paint — otherwise they'd flash at full opacity
  // for one frame before the cascade hides them.
  useLayoutEffect(() => {
    if (revealSeenGameIds === null) return // reveal-seen not loaded yet

    const batch: RevealInput[] = []
    for (const game of games) {
      const settled = resultsRevealed || simGameStatuses?.[game.id] === 'final'
      const actual = actualResults[game.id]
      if (!settled || !actual) continue
      if (revealSeenGameIds.has(game.id) || revealStartedRef.current.has(game.id)) {
        continue
      }
      const prediction = predictions[game.id]
      const basePoints = prediction ? scorePrediction(prediction, actual).points : 0
      batch.push({ gameId: game.id, category: categoryForBasePoints(basePoints) })
    }
    if (batch.length === 0) return

    for (const item of batch) revealStartedRef.current.add(item.gameId)
    const reduced = prefersReducedMotion()
    const plan = computeRevealPlan(batch, reduced)

    const later = (ms: number, fn: () => void) => {
      revealTimersRef.current.push(
        setTimeout(() => {
          if (mountedRef.current) fn()
        }, ms)
      )
    }
    // Exact scores are remembered for the session only (ticket 0418), so the
    // detonation plays again on a fresh app open; the quieter tiers stay
    // persisted once-ever, because a grid of misses re-landing on every refresh
    // gets old fast.
    const mark = (ids: string[], persist = true) => {
      if (mountedRef.current) onMarkRevealSeenRef.current(ids, persist)
    }
    const toDone = (gameId: string) =>
      setRevealPhaseById((prev) => {
        const next = { ...prev }
        delete next[gameId]
        return next
      })

    // Reduced motion: no stagger, no spotlight, no effects. Cards appear lit at
    // final value; mark everything at once.
    if (reduced) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRevealPhaseById((prev) => {
        const next = { ...prev }
        for (const item of batch) next[item.gameId] = 'active'
        return next
      })
      mark(
        batch.filter((b) => b.category !== 'exact').map((b) => b.gameId),
        true
      )
      mark(
        batch.filter((b) => b.category === 'exact').map((b) => b.gameId),
        false
      )
      later(50, () => batch.forEach((b) => toDone(b.gameId)))
      return
    }

    // Hide the whole batch (pending) before paint; each card lands on its turn.
    setRevealPhaseById((prev) => {
      const next = { ...prev }
      for (const item of batch) next[item.gameId] = 'pending'
      return next
    })
    for (const item of batch) pendingRevealIdsRef.current.add(item.gameId)

    const exactIds = batch.filter((b) => b.category === 'exact').map((b) => b.gameId)
    if (plan.volley) {
      setVolleyedIds((prev) => new Set([...prev, ...exactIds]))
    }

    // Positions a shockwave + particle burst over one or more target cards'
    // grid rects (measured relative to the effects layer — no fixed positioning,
    // so no transformed-ancestor trap). A shared volley burst sits at the group
    // centroid; solo exacts get their own burst.
    const spawnBurst = (ids: string[], shared: boolean) => {
      const layer = revealLayerRef.current
      if (!layer) return
      const lr = layer.getBoundingClientRect()
      const centers = ids
        .map((id) => cardRefs.current.get(id)?.getBoundingClientRect())
        .filter((r): r is DOMRect => r != null)
        .map((r) => ({ x: r.left - lr.left + r.width / 2, y: r.top - lr.top + r.height / 2, w: r.width }))
      if (centers.length === 0) return

      const push = (cx: number, cy: number, size: number, volley: boolean) => {
        const key = `burst-${burstKeyRef.current++}`
        setBursts((prev) => [...prev, { key, cx, cy, size, volley }])
        later(BURST_LIFETIME_MS, () =>
          setBursts((prev) => prev.filter((b) => b.key !== key))
        )
      }
      if (shared) {
        const cx = centers.reduce((s, c) => s + c.x, 0) / centers.length
        const cy = centers.reduce((s, c) => s + c.y, 0) / centers.length
        push(cx, cy, Math.max(...centers.map((c) => c.w)), true)
      } else {
        centers.forEach((c) => push(c.x, c.y, c.w, false))
      }
    }

    // Each card's scheduled start — the single moment its CSS flourish begins
    // and (for a solo exact) its spotlight + burst fire together.
    for (const item of batch) {
      const entry = plan.entries[item.gameId]
      const activeMs =
        item.category === 'exact'
          ? plan.volley
            ? VOLLEY_ACTIVE_MS
            : REVEAL_ACTIVE_MS.exact
          : REVEAL_ACTIVE_MS[item.category]

      later(entry.delay, () => {
        setRevealPhaseById((prev) => ({ ...prev, [item.gameId]: 'active' }))
        pendingRevealIdsRef.current.delete(item.gameId)
        // Non-exacts mark at their start (nothing meaningful to miss). Solo
        // exacts spotlight now and mark when the slam lands; volleys are handled
        // once, below, off the shared start.
        if (item.category !== 'exact') {
          mark([item.gameId])
        } else if (!plan.volley) {
          setSpotlitIds((prev) => new Set(prev).add(item.gameId))
          spawnBurst([item.gameId], false)
          later(PHASE_A_SLAM_LAND_MS, () => mark([item.gameId], false))
          later(SPOTLIGHT_SOLO_MS, () =>
            setSpotlitIds((prev) => {
              const next = new Set(prev)
              next.delete(item.gameId)
              return next
            })
          )
        }
        later(activeMs, () => toDone(item.gameId))
      })
    }

    // Volley: one shared spotlight + one shared burst + one shared mark, off the
    // first volleyed exact's start. All volleyed cards mark together or not at
    // all — a half-marked volley would replay as an ugly partial volley.
    if (plan.volley && exactIds.length > 0) {
      const groupStart = Math.min(...exactIds.map((id) => plan.entries[id].delay))
      later(groupStart, () => {
        setSpotlitIds((prev) => new Set([...prev, ...exactIds]))
        spawnBurst(exactIds, true)
        later(PHASE_A_SLAM_LAND_MS, () => mark(exactIds, false))
        later(SPOTLIGHT_VOLLEY_MS, () =>
          setSpotlitIds((prev) => {
            const next = new Set(prev)
            for (const id of exactIds) next.delete(id)
            return next
          })
        )
      })
    }
  }, [games, predictions, actualResults, resultsRevealed, simGameStatuses, revealSeenGameIds])

  const registerCard = (gameId: string, el: HTMLButtonElement | null) => {
    if (el) cardRefs.current.set(gameId, el)
    else cardRefs.current.delete(gameId)
  }

  return { revealPhaseById, spotlitIds, volleyedIds, bursts, registerCard, revealLayerRef }
}
